// src/oura/merchant/Decisions.tsx
//
// Decisions: everything we asked the merchant, and the work that needs
// nothing from them. One sentence says how many need their reply and how
// many are late; "Start answering" walks them all in the drawer, one at a
// time. The page is the list; every answer is given in the drawer.
//
// Three cards: Questions for you, Your documents (Documents.tsx) and In hand.
// Each lists its rows in groups by who moves next, a firm line between
// groups: Needs your reply open, everything else folded. A row is 1 line: a
// dot, the question, and its one fact (a date; late in rose). The group head
// says the state. On screen an ask is a "question" (YW, 29 Sep 2026); their
// own requests are on Work.
//
// `?item=<id>` is the link in every notification email and in the bell: it
// unfolds the question's card and group and opens it. `?doc=<id>` does the
// same for a shared document, reading the page once more first when the doc
// is not on it yet. Both then leave the URL, so a reload does not open them
// again. `#documents` unfolds the documents card.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Access } from "./Access";
import { useSnackbar } from "@hook/use-snackbar";
import { ChevronDownIcon, ClipboardCheckIcon, HistoryIcon, InboxIcon, ListChecksIcon, MessageCircleQuestionIcon } from "lucide-react";
import type { ClientPagePayload, ClientPlanItem, Thread } from "@service/aeo/clientReview";
import type { MerchantDocument } from "@service/aeo/clientDocs";
import {
    CardEmpty,
    Dot,
    Drawer,
    DrawerBlock,
    DrawerHead,
    FOCUS_RING,
    FoldCard,
    FoldScope,
    GroupFold,
    LATE_TEXT,
    ListRow,
    MERCHANT_STATUS,
    PageHead,
    PillButton,
    SURFACE,
    Seg,
    ShowMore,
    TYPE,
    Tag,
    returnFocusChain,
    useFoldOpener,
    type SegItem,
    ROW_HOVER_TIGHT,
    ArrowLink,
} from "@oura/ui";
import { cn } from "../lib/cn";
import { ContradictionEvidenceBlock, contradictionEvidence } from "../components/ContradictionEvidence";
import { useMerchantDrawers } from "./useMerchantDrawers";
import { useAnswersEmptiedFocus } from "./drawers/answersEmptied";
import { DocumentsCard, MerchantDocRow } from "./Documents";
import { PanelMark, WorkProgress } from "./HomeMonth";
import { completedText, needsYouQueue, pendingCounts, pendingText, type QueueEntry } from "./docAsks";
import { setupPart, useMySetup } from "../hooks/useMySetup";
import { Sentence, StartAnswering } from "./StartAnswering";
import { CLIENT_HINT, OURA_ROOT } from "../nav";
import { clientArea, CLIENT_PAGE_SENTENCE, MERCHANT_V2 } from "../merchantV2";
import {
    ASK_GROUP,
    ASK_GROUP_ORDER,
    askFact,
    askGroup,
    asksOf,
    byUrgency,
    isOpen,
    lastStaffMessage,
    lateDaysOf,
    plural,
    type Ask,
    type AskGroup,
} from "./asks";

type InHandItem = ClientPagePayload["noDecisionNeeded"][number];

/** Where each piece is, in a line. Its word, dot and greying are the plan's own (MERCHANT_STATUS, D8). */
const IN_HAND_LINE: Record<InHandItem["status"], string> = {
    in_progress: "We are on it.",
    not_started: "Planned. We have not started it yet.",
    done: "Done.",
};
const IN_HAND_ORDER: InHandItem["status"][] = ["in_progress", "not_started", "done"];

/** Answered questions shown before "Show N more". */
const ANSWERED_SHOWN = 3;

export function Decisions({ payload, focusItemId }: { payload: ClientPagePayload; focusItemId?: string | null }) {
    const drawers = useMerchantDrawers();
    const { readyDraftIds } = drawers;
    const { documents, refreshFailed, reads, refresh } = drawers.docs;
    const snackbar = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const { hash } = useLocation();
    const openFold = useFoldOpener("merchant-decisions");

    // Which tab the four-tab To do shows (#4062 MR-FE-6). Deep links pick it
    // before the drawer opens, so ?doc= lands on Documents and ?item= on
    // whichever tab that item is actually in.
    // `?tab=setup` opens on Setup: where Home's "Start setup" sends them.
    const [tab, setTab] = useState<ToDoTab>(() => (MERCHANT_V2 && searchParams.get("tab") === "setup" ? "setup" : "answer"));
    // The account menu's Setup links here with the page already open, so the
    // tab follows the link too, not only the first load.
    const tabParam = searchParams.get("tab");
    useEffect(() => {
        if (MERCHANT_V2 && tabParam === "setup") setTab("setup");
    }, [tabParam]);

    const asks = useMemo(() => asksOf(payload.packs), [payload.packs]);
    const threadByItem = useMemo(() => new Map(payload.threads.map((t) => [t.itemId, t])), [payload.threads]);
    const grouped = useMemo(() => {
        const out: Record<AskGroup, Ask[]> = { needs: [], revising: [], later: [], answered: [] };
        for (const ask of asks) out[askGroup(ask.item)].push(ask);
        out.needs.sort(byUrgency);
        // Newest decision first: the one they just sent is the one they look for.
        out.answered.sort((a, b) => (b.item.decidedAt ?? "").localeCompare(a.item.decidedAt ?? ""));
        return out;
    }, [asks]);

    // The deep link. Consumed once: the card and group unfold, the drawer
    // opens, and `item` leaves the URL. A link to a question that is no longer
    // on the page (withdrawn, or from another business) says so rather than
    // landing on the plain list. `linked` holds the link being handled until
    // the URL has dropped it: the snackbar re-renders its provider, which
    // hands out a new `snackbar`, and without it every render in between
    // showed the note again.
    const linked = useRef<string | null>(null);
    useEffect(() => {
        if (!focusItemId) {
            linked.current = null;
            return;
        }
        if (linked.current === focusItemId) return;
        linked.current = focusItemId;
        const ask = asks.find((a) => a.item.id === focusItemId);
        if (ask) {
            // An item lives in exactly one tab, and it is not always the one
            // on screen: an answered item is in History, a change request in
            // With us (#4062 MR-FE-6).
            setTab(TAB_OF_GROUP[askGroup(ask.item)]);
            // Unfold first, so closing the drawer can return to the row.
            openFold("asks");
            openFold(`asks.${askGroup(ask.item)}`);
            drawers.openQuestion(focusItemId);
        } else snackbar.showSuccess("That question is no longer open.", "Everything still open is on this page.");
        setSearchParams(
            (params) => {
                params.delete("item");
                return params;
            },
            { replace: true },
        );
    }, [focusItemId, asks, drawers, openFold, setSearchParams, snackbar]);

    // `?doc=<id>`, consumed once like `?item=`. A doc missing from the list on
    // screen may have been shared since the page was read, so the page is read
    // once more before the link is called stale. `docLink` holds the link being
    // handled (and the read count it waits past) until the URL drops it.
    const docParam = searchParams.get("doc");
    const docLink = useRef<{ id: string; after: number; done: boolean } | null>(null);
    useEffect(() => {
        if (!docParam) {
            docLink.current = null;
            return;
        }
        if (docLink.current?.id === docParam && docLink.current.done) return;
        const drop = () => {
            if (docLink.current) docLink.current.done = true;
            setSearchParams(
                (params) => {
                    params.delete("doc");
                    return params;
                },
                { replace: true },
            );
        };
        const doc = documents?.find((d) => d.id === docParam);
        if (doc) {
            docLink.current = { id: docParam, after: reads, done: false };
            // The tab first, so closing the drawer returns to a row that is on
            // screen rather than behind another tab (#4062 MR-FE-6).
            // The tab the doc lives in: To answer while it needs them, With us
            // while we revise it. Anything else is only in Documents, and the
            // drawer opens over whichever tab is showing.
            if (doc.state === "needs_your_reply") setTab("answer");
            else if (doc.state === "with_us") setTab("with_us");
            // Unfold the card and the doc's group first, so closing the drawer can return to the row.
            openFold("documents");
            openFold(`documents.${doc.state}`);
            drawers.openDoc(docParam);
            drop();
            return;
        }
        if (docLink.current?.id !== docParam) {
            docLink.current = { id: docParam, after: reads, done: false };
            refresh();
            return;
        }
        // Still waiting for that read.
        if (reads === docLink.current.after) return;
        if (refreshFailed) snackbar.showError("Could not open that document.", "Try the link again in a minute.");
        // No documents on the page at all: Oura does not show them for this business yet, which is not "no longer shared".
        else if (documents === undefined) snackbar.showSuccess("Your documents don't show here yet.", "They come to you by email.");
        else snackbar.showSuccess("That document is no longer shared.", "Everything shared with you is on this page.");
        drop();
    }, [docParam, documents, reads, refresh, refreshFailed, drawers, openFold, setSearchParams, snackbar]);

    // The queue ran out on Done: the rows it opened from have moved, so focus
    // goes to the Questions for you card's head, which stays put.
    const focusAsks = useCallback(() => {
        openFold("asks");
        return document.getElementById("asks")?.contains(document.activeElement) ?? false;
    }, [openFold]);
    useAnswersEmptiedFocus(focusAsks);

    // `/oura/decisions#documents`: unfold the card as well as scroll to it.
    useEffect(() => {
        if (hash !== "#documents") return;
        openFold("documents");
    }, [hash, openFold]);

    // The same two figures Home shows and the Decisions badge counts (#4026
    // C-FE-6), from the one function, so no two of the three can disagree.
    const setup = setupPart(useMySetup());
    // While set-up steps are left, the page opens on Setup, the first tab. Only
    // on arrival: a link that names a tab or an item, or a press, wins.
    const landed = useRef(Boolean(tabParam || searchParams.get("item") || docParam || hash));
    const setupLeft = setup.known ? setup.mine : null;
    useEffect(() => {
        if (landed.current || !MERCHANT_V2 || setupLeft === null) return;
        landed.current = true;
        if (setupLeft > 0) setTab((was) => (was === "answer" ? "setup" : was));
    }, [setupLeft]);
    // The address says when Setup is open, however it opened (a link, a
    // press, or the arrival above), so the shell's "Finish setup" card hides
    // while the setup steps it points to are already on screen.
    useEffect(() => {
        if (!MERCHANT_V2 || (tab === "setup") === (tabParam === "setup")) return;
        setSearchParams((was) => {
            const next = new URLSearchParams(was);
            if (tab === "setup") next.set("tab", "setup");
            else next.delete("tab");
            return next;
        }, { replace: true });
    }, [tab, tabParam, setSearchParams]);
    const counts = pendingCounts(payload, setup);
    const needs = grouped.needs;
    const lateAsks = needs.filter((a) => lateDaysOf(a.item) > 0).length;
    const lateNote = lateAsks > 0 ? <span className={LATE_TEXT}>{lateAsks} late</span> : null;

    return (
        <FoldScope value="merchant-decisions">
            {/* "To do", one fixed sentence, and no counts: the number of
                questions waiting is on the menu badge and on the rows, and
                saying it a third time here is what made five names for one
                idea (#4062 MR-FE-3). The button stays, because this is the
                page that has one. */}
            <PageHead
                over={payload.business.name}
                title={MERCHANT_V2 ? "To do" : "Decisions"}
                sentence={MERCHANT_V2 ? CLIENT_PAGE_SENTENCE.decisions : CLIENT_HINT.decisions}
                status={MERCHANT_V2 ? undefined : (
                    <>
                        <Sentence parts={pendingText(counts)} />
                        {" · "}
                        {completedText(counts)}
                    </>
                )}
                // On Setup the step's own button is the one to press, so a
                // second blue button in the header only competes with it.
                actions={MERCHANT_V2 && tab === "setup" ? undefined : <StartAnswering />}
            />
            {MERCHANT_V2 ? (
                <ToDoTabs
                    payload={payload}
                    grouped={grouped}
                    documents={documents}
                    threadByItem={threadByItem}
                    readyDraftIds={readyDraftIds}
                    drawers={drawers}
                    tab={tab}
                    onTab={(t) => {
                        landed.current = true;
                        setTab(t);
                    }}
                    setupMine={setupLeft}
                />
            ) : (
            <div className="space-y-5">
                {/* With no questions here and documents shared, the card would
                    only say there are none beside documents that have some. */}
                {(asks.length > 0 || !documents || documents.length === 0) && (
                    <FoldCard
                        id="asks"
                        anchor="asks"
                        title="Questions for you"
                        icon={{ icon: MessageCircleQuestionIcon, area: clientArea("asks") }}
                        foldedFact={
                            needs.length > 0 ? (
                                <span className="tabular-nums">
                                    {needs.length} {needs.length === 1 ? "needs" : "need"} your reply
                                    {lateNote && <> · {lateNote}</>}
                                </span>
                            ) : (
                                // A number a reader can compare with
                                // yesterday's, not an apology (#4026 C-FE-6).
                                "0 pending"
                            )
                        }
                    >
                        {asks.length === 0 ? (
                            // The page with MERCHANT_V2 off keeps its old line;
                            // the redesign's empty states live in ToDoTabs.
                            <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No questions yet. We will tell you here, and by email, when something needs you.</p>
                        ) : (
                            // The groups are the only children of this box, so the first loses its divider.
                            <div>
                                {ASK_GROUP_ORDER.filter((key) => grouped[key].length > 0).map((key) => {
                                    const list = grouped[key];
                                    const shown = key === "answered" ? list.slice(0, ANSWERED_SHOWN) : list;
                                    const row = (ask: Ask) => (
                                        <AskRow
                                            key={ask.item.id}
                                            ask={ask}
                                            group={key}
                                            thread={threadByItem.get(ask.item.id) ?? null}
                                            ready={readyDraftIds.has(ask.item.id)}
                                            onOpen={drawers.openQuestion}
                                        />
                                    );
                                    return (
                                        <GroupFold
                                            key={key}
                                            id={`asks.${key}`}
                                            lead="dot"
                                            tone={ASK_GROUP[key].tone}
                                            title={ASK_GROUP[key].label}
                                            count={list.length}
                                            defaultFolded={key !== "needs"}
                                            note={key === "needs" ? lateNote : undefined}
                                        >
                                            <div>{shown.map(row)}</div>
                                            <ShowMore count={list.length - shown.length}>{list.slice(shown.length).map(row)}</ShowMore>
                                        </GroupFold>
                                    );
                                })}
                            </div>
                        )}
                    </FoldCard>
                )}

                <DocumentsCard documents={documents} refreshFailed={refreshFailed} onOpen={drawers.openDoc} />

                {payload.noDecisionNeeded.length > 0 && <InHand items={payload.noDecisionNeeded} />}
            </div>
            )}
        </FoldScope>
    );
}

/* ------------------------------------------------------------------------- *
 * To do: four tabs (#4062 MR-FE-6)
 * ------------------------------------------------------------------------- */

type ToDoTab = "answer" | "with_us" | "history" | "setup";

/** Where each of today's groups lives once they are four tabs. */
const TAB_OF_GROUP: Record<AskGroup, ToDoTab> = {
    needs: "answer",
    revising: "with_us",
    later: "history",
    answered: "history",
};

/**
 * Three stacked cards of folded groups become one card with four tabs.
 *
 * The old screen put six groups behind folds across three cards, and the one
 * that matters -- what is waiting on the client -- was a fold among them. A
 * tab carries its own count, is always one press away, and cannot be scrolled
 * past.
 *
 * The groups are not re-invented: each tab holds exactly what a group held,
 * in the same order, with the same rows.
 */
function ToDoTabs({
    payload,
    grouped,
    documents,
    threadByItem,
    readyDraftIds,
    drawers,
    tab,
    onTab,
    setupMine,
}: {
    payload: ClientPagePayload;
    grouped: Record<AskGroup, Ask[]>;
    documents: MerchantDocument[] | undefined;
    threadByItem: Map<string, Thread>;
    readyDraftIds: ReadonlySet<string>;
    drawers: ReturnType<typeof useMerchantDrawers>;
    tab: ToDoTab;
    onTab: (t: ToDoTab) => void;
    /**
     * Set-up steps still the client's: counted on the menu badge, so said here too (#4132).
     * Null while that count is not known (loading, or the request failed): never a zero.
     */
    setupMine: number | null;
}) {
    // To answer is the queue, in the queue's own order, so the tab, the menu
    // badge and "Start answering" are the same list counted once.
    const queue = useMemo(() => needsYouQueue(payload), [payload]);
    // To do holds only what needs them, or is moving with us (22 Oct 2026
    // feedback): a doc that needs their reply is in the queue, one we are
    // revising is With us, and every doc, whatever its state, is in Documents.
    const docsWithUs = (documents ?? []).filter((d) => d.state === "with_us");
    const history = [...grouped.later, ...grouped.answered];
    const withUs = grouped.revising;
    // A finding with its evidence is theirs to see here; the plan's work is on
    // Our work, and this tab points to it (27 Oct 2026).
    const found = payload.noDecisionNeeded.filter((w) => contradictionEvidence(w) !== null);
    const planItems = payload.plan && payload.subscription ? payload.plan.items : [];
    const workOpen = planItems.filter((i) => i.status !== "done" && i.status !== "waiting_on_you").length;

    const tabs: SegItem<ToDoTab>[] = [
        // Set-up is To do while steps are left (the redesign's five-item
        // rail took it out of the menu), and first: nothing else moves until
        // it is done. Once it is done the tab goes; the account menu keeps the
        // page for changing or removing access.
        ...(setupMine && setupMine > 0 || tab === "setup" ? [{ key: "setup" as const, label: "Setup", icon: ListChecksIcon, count: setupMine ?? undefined }] : []),
        { key: "answer", label: "To answer", icon: MessageCircleQuestionIcon, count: queue.length },
        { key: "with_us", label: "With us", icon: InboxIcon, count: withUs.length + found.length + docsWithUs.length + workOpen },
        { key: "history", label: "History", icon: HistoryIcon, count: history.length },
    ];

    const askRow = (ask: Ask, group: AskGroup) => (
        <AskRow
            key={ask.item.id}
            ask={ask}
            group={group}
            thread={threadByItem.get(ask.item.id) ?? null}
            ready={readyDraftIds.has(ask.item.id)}
            onOpen={drawers.openQuestion}
        />
    );

    const strip = (
        <Seg<ToDoTab>
            as="tablist"
            ariaLabel="What is waiting, and what is done"
            items={tabs}
            value={tab}
            onChange={onTab}
            panelId="todo-panel"
        />
    );

    // Setup is its own set of cards (a Next card, the step, the side column),
    // so it sits on the page ground under the tabs, not inside this card.
    if (tab === "setup") {
        return (
            <div className="space-y-5">
                <div className={cn(SURFACE.card, "p-4 sm:p-5")}>{strip}</div>
                <div id="todo-panel">
                    <Access embedded />
                </div>
            </div>
        );
    }

    return (
        <div className={cn(SURFACE.card, "p-4 sm:p-5")}>
            {strip}
            <div id="todo-panel" className="mt-4">
                {tab === "answer" && (
                    queue.length === 0 ? (
                        setupMine === null ? (
                            // Not known yet, so not "all caught up": that is only true once
                            // the set-up count has been read and is zero.
                            <CardEmpty icon={ClipboardCheckIcon} title="Nothing to answer" />
                        ) : setupMine > 0 ? (
                            // The badge counts their own set-up steps, so "all caught up" under a
                            // "1" was a contradiction (#4132): say what is left, and where.
                            <CardEmpty
                                icon={ClipboardCheckIcon}
                                title="Nothing to answer"
                                action={
                                    <PillButton variant="primary" onClick={() => onTab("setup")}>
                                        Finish setup
                                    </PillButton>
                                }
                            >
                                {setupMine === 1 ? "1 set-up step is" : `${setupMine} set-up steps are`} still yours.
                            </CardEmpty>
                        ) : (
                            <CardEmpty icon={ClipboardCheckIcon} title="You are all caught up">
                                We will email you when we need something.
                            </CardEmpty>
                        )
                    ) : (
                        <div>
                            {queue.map((e: QueueEntry) =>
                                e.kind === "question"
                                    ? askRow(e.ask, "needs")
                                    : <DocLine key={e.id} doc={e.doc} onOpen={() => drawers.openDoc(e.id)} />,
                            )}
                        </div>
                    )
                )}

                {tab === "with_us" && (
                    withUs.length === 0 && found.length === 0 && docsWithUs.length === 0 && planItems.length === 0 ? (
                        <CardEmpty icon={InboxIcon} title="Nothing is with us right now">
                            When you answer a question or ask for a change, you can follow it here.
                        </CardEmpty>
                    ) : (
                        <div className="space-y-4">
                            {(withUs.length > 0 || found.length > 0) && (
                                <Group title="Your changes we are making">
                                    <WithUsBoard
                                        asks={withUs}
                                        items={found}
                                        threadOf={(id) => threadByItem.get(id) ?? null}
                                        onOpenAsk={drawers.openQuestion}
                                    />
                                </Group>
                            )}
                            {docsWithUs.length > 0 && (
                                <Group title="Documents we are revising">
                                    <div>
                                        {docsWithUs.map((d) => (
                                            <DocLine key={d.id} doc={d} onOpen={() => drawers.openDoc(d.id)} />
                                        ))}
                                    </div>
                                </Group>
                            )}
                            {planItems.length > 0 && <WorkPointer items={planItems} schedule={payload.schedule} />}
                        </div>
                    )
                )}

                {tab === "history" && (
                    history.length === 0 ? (
                        <CardEmpty icon={HistoryIcon} title="No answers yet">
                            Everything you answer is kept here, so you can look back at it.
                        </CardEmpty>
                    ) : (
                        <div className="space-y-4">
                            {grouped.later.length > 0 && (
                                <Group title="Saved for later">
                                    <div>{grouped.later.map((a) => askRow(a, "later"))}</div>
                                </Group>
                            )}
                            {grouped.answered.length > 0 && (
                                <Group title="Answered">
                                    <div>{grouped.answered.slice(0, ANSWERED_SHOWN).map((a) => askRow(a, "answered"))}</div>
                                    <ShowMore count={grouped.answered.length - ANSWERED_SHOWN}>
                                        {grouped.answered.slice(ANSWERED_SHOWN).map((a) => askRow(a, "answered"))}
                                    </ShowMore>
                                </Group>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

/** A document in the To answer queue: the same row the Documents tab draws. */
function DocLine({ doc, onOpen }: { doc: MerchantDocument; onOpen: () => void }) {
    return <MerchantDocRow doc={doc} onOpen={onOpen} />;
}

/**
 * The plan's work, said once and pointed to (27 Oct 2026): how far it has
 * got, in Home's progress line, and the way to Our work, where the list is.
 */
function WorkPointer({ items, schedule }: { items: ClientPlanItem[]; schedule: ClientPagePayload["schedule"] }) {
    return (
        <section className="flex flex-col gap-3 rounded-xl bg-gray-50 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>Our work for you</h3>
                <ArrowLink to={`${OURA_ROOT}/work`}>See what we are doing</ArrowLink>
            </div>
            <WorkProgress items={items} schedule={schedule} />
        </section>
    );
}

/** A heading inside a tab. Plain, because the tab is the card. */
function Group({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section>
            <h3 className={cn(TYPE.lab, "m-0 mb-1")}>{title}</h3>
            {children}
        </section>
    );
}

/**
 * One question: its dot, the question, and one fact (a date; late in rose).
 * A reply of ours waiting on them adds our words as a second line, since
 * that is what they would answer. "Ready to send" when an answer is written
 * and not sent.
 */
function AskRow({
    ask,
    group,
    thread,
    ready,
    onOpen,
}: {
    ask: Ask;
    group: AskGroup;
    thread: Thread | null;
    ready: boolean;
    onOpen: (itemId: string) => void;
}) {
    const { item } = ask;
    const fact = askFact(ask, thread);
    const ours = group === "needs" && !isOpen(item) ? lastStaffMessage(thread) : null;
    const quote = ours?.body.trim() ? `${ours.authorName}: “${ours.body.trim()}”` : null;
    return (
        <ListRow
            // Found again by the drawer's close: an answer moves the row to another group.
            data-ask-id={item.id}
            lead="dot"
            stackMeta
            icon={<Dot tone={ASK_GROUP[group].tone} />}
            title={item.title}
            srStatus={ASK_GROUP[group].label}
            sub={
                quote && (
                    <span title={quote} className="block truncate text-gray-600">
                        {quote}
                    </span>
                )
            }
            tags={ready ? <Tag tone="blue">Ready to send</Tag> : undefined}
            meta={fact && !fact.late ? fact.text : undefined}
            late={fact?.late ? fact.text : undefined}
            muted={group === "later"}
            onClick={() => onOpen(item.id)}
        />
    );
}

/**
 * The work in the plan that needs nothing from them, grouped by where each
 * piece is: In progress open, Not started and Done folded. Every row opens a
 * small drawer: its state, and the two quotes when it is a contradiction we
 * are correcting. The card starts folded: this screen is about what needs
 * them, and the folded head still says nothing is needed.
 */
function InHand({ items }: { items: InHandItem[] }) {
    const [openLabel, setOpenLabel] = useState<string | null>(null);
    const opened = items.find((w) => w.label === openLabel) ?? null;
    return (
        <>
            <FoldCard
                id="in-hand"
                defaultFolded
                title="In hand"
                icon={{ icon: ClipboardCheckIcon, area: clientArea("work") }}
                foldedFact={`${plural(items.length, "thing")}, nothing needed from you`}
            >
                <div>
                    {IN_HAND_ORDER.map((status) => {
                        const list = items.filter((w) => w.status === status);
                        if (list.length === 0) return null;
                        const style = MERCHANT_STATUS[status];
                        return (
                            <GroupFold
                                key={status}
                                id={`in-hand.${status}`}
                                lead="dot"
                                tone={style.tone}
                                title={style.label}
                                count={list.length}
                                defaultFolded={status !== "in_progress"}
                            >
                                <div>
                                    {list.map((w) => (
                                        <ListRow
                                            key={w.label}
                                            data-in-hand={w.label}
                                            lead="dot"
                                            icon={<Dot tone={style.tone} />}
                                            title={w.label}
                                            srStatus={style.label}
                                            muted={style.muted}
                                            onClick={() => setOpenLabel(w.label)}
                                        />
                                    ))}
                                </div>
                            </GroupFold>
                        );
                    })}
                </div>
            </FoldCard>
            <InHandDrawer item={opened} onClose={() => setOpenLabel(null)} />
        </>
    );
}

/** The board's columns, in the order work moves through them. */
const BOARD_ORDER: InHandItem["status"][] = ["in_progress", "not_started", "done"];

/**
 * What is with us that is theirs (27 Oct 2026): the changes they asked for
 * that we are making, and any finding with its evidence. One list, in the
 * marks Our work's list uses. The plan's work is on Our work, once
 * (WorkList.tsx); this tab points there rather than drawing it again.
 *
 * A change they asked for that we are making goes in In progress, first,
 * tagged "Your change", since it is theirs, and opens its question. A piece
 * with the evidence of a mismatch opens in place to show it; the rest are
 * one line with nothing more to open.
 */
function WithUsBoard({
    asks,
    items,
    threadOf,
    onOpenAsk,
}: {
    asks: Ask[];
    items: InHandItem[];
    threadOf: (itemId: string) => Thread | null;
    onOpenAsk: (itemId: string) => void;
}) {
    // Any number open at once: opening one never closes another.
    const [openLabels, setOpenLabels] = useState<ReadonlySet<string>>(() => new Set());
    const toggle = (label: string) =>
        setOpenLabels((was) => {
            const next = new Set(was);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
    return (
        <ul className="list-plain m-0 flex flex-col gap-3 p-0">
            {BOARD_ORDER.map((status) => {
                const changes = status === "in_progress" ? asks : [];
                const list = items.filter((w) => w.status === status);
                return (
                    <Fragment key={status}>
                        {[
                            ...changes.map((a) => {
                                const fact = askFact(a, threadOf(a.item.id));
                                return (
                                    <li key={a.item.id} className="flex items-start gap-2.5">
                                        <PanelMark status={status} />
                                        <button
                                            type="button"
                                            data-ask-id={a.item.id}
                                            onClick={() => onOpenAsk(a.item.id)}
                                            className={cn("min-w-0 flex-1 rounded text-left", ROW_HOVER_TIGHT, FOCUS_RING)}
                                        >
                                            <span className={cn(TYPE.tb, "block break-words text-gray-900 group-hover:text-blue-600")}>{a.item.title}</span>
                                            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                                <Tag tone="blue">Your change</Tag>
                                                {fact && <span className={cn(TYPE.tc, fact.late ? LATE_TEXT : "text-gray-500")}>{fact.text}</span>}
                                            </span>
                                        </button>
                                    </li>
                                );
                            }),
                            ...list.map((w) => {
                                const evidence = contradictionEvidence(w);
                                const open = openLabels.has(w.label);
                                const detailId = `with-us-${status}-${w.label}`;
                                return (
                                    // The column already says the status, so a piece opens
                                    // only when it has something of its own to show: the
                                    // evidence of a mismatch (16 Oct 2026).
                                    <li key={w.label} data-in-hand={w.label} className="flex items-start gap-2.5">
                                        <PanelMark status={status} />
                                        {evidence ? (
                                            <div className="min-w-0 flex-1">
                                                <button
                                                    type="button"
                                                    aria-expanded={open}
                                                    aria-controls={detailId}
                                                    onClick={() => toggle(w.label)}
                                                    className={cn("flex w-full items-start gap-2 rounded text-left", ROW_HOVER_TIGHT, FOCUS_RING)}
                                                >
                                                    <span className={cn(TYPE.tb, "min-w-0 flex-1 break-words text-gray-900 group-hover:text-blue-600")}>{w.label}</span>
                                                    <ChevronDownIcon
                                                        aria-hidden
                                                        size={14}
                                                        className={cn("mt-1 shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none", open && "rotate-180")}
                                                    />
                                                </button>
                                                {open && (
                                                    <div id={detailId} className={cn(TYPE.tc, "mt-2 rounded-lg bg-white p-3 text-gray-700 ring-1 ring-inset ring-black/[0.06]")}>
                                                        <ContradictionEvidenceBlock evidence={evidence} />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className={cn(TYPE.tb, "min-w-0 break-words text-gray-900")}>{w.label}</span>
                                        )}
                                    </li>
                                );
                            }),
                        ]}
                    </Fragment>
                );
            })}
        </ul>
    );
}

function InHandDrawer({ item, onClose }: { item: InHandItem | null; onClose: () => void }) {
    if (!item) return null;
    const contradiction = contradictionEvidence(item);
    const status = MERCHANT_STATUS[item.status];
    return (
        <Drawer open onClose={onClose} returnFocus={returnFocusChain(`[data-in-hand="${CSS.escape(item.label)}"]`, `[data-group-head="in-hand.${item.status}"]`)}>
            <DrawerHead title={item.label} status={{ tone: status.tone, label: status.label }} />
            <DrawerBlock>
                <p className="m-0">
                    {IN_HAND_LINE[item.status]} It needs nothing from you.
                </p>
            </DrawerBlock>
            {/* The same block staff see, from the same narrowing reader
                (#3760 AC5). Null for every other kind of task. */}
            {contradiction && (
                <DrawerBlock label="What an assistant said">
                    <ContradictionEvidenceBlock evidence={contradiction} className="mt-2" />
                </DrawerBlock>
            )}
        </Drawer>
    );
}
